import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';

import FormContainer from 'components/LoanForm/FormContainer';
import Button from 'components/Button';
import Input from 'components/Input';
import NumberInput from 'components/NumberInput';
import { useNavigate } from 'hooks/useNavigate';
import { getApplicationData } from 'selectors/getApplicationData';
import { NON_DIGITS_PATTERN } from 'components/Input/validation';
import { RoutePath } from 'enums/Routes';
import { getMessageForInvalidFields } from 'utils/errors';

import styles from './ExtendedIdentityCheck.module.scss';

enum Field {
  MiddleName = 'middleName',
  SocialSecurityNumber = 'socialSecurityNumber',
}

const SOCIAL_SECURITY_LENGTH_PATTERN = /^.{9}$/;

enum CreditReportInputLabel {
  SocialSecurityNumber = 'Social Security number',
}

export interface ExtendedIdentityFormState {
  middleName?: string;
  socialSecurityNumber?: string;
}

const ExtendedIdentityCheck = (): JSX.Element => {
  const navigate = useNavigate();
  const { application } = useSelector(getApplicationData);
  const last4SSN = application?.borrowerLast4SSN;

  const {
    register,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<Record<Field, string>>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      [Field.MiddleName]: '',
      [Field.SocialSecurityNumber]: '',
    },
  });

  const watcher = watch();

  useEffect(() => {
    register(Field.MiddleName);
    register(Field.SocialSecurityNumber, {
      pattern: {
        message: getMessageForInvalidFields(CreditReportInputLabel.SocialSecurityNumber),
        value: SOCIAL_SECURITY_LENGTH_PATTERN,
      },
      validate: (digits: string) => {
        if (digits && last4SSN && digits.slice(-4) !== last4SSN) {
          return 'Social Security number must match the last 4 digits you entered earlier';
        }
        return true;
      },
    });
  }, [register, last4SSN]);

  const handleContinue = async () => {
    const isValid = await trigger(Field.SocialSecurityNumber);
    if (!isValid) {
      return;
    }

    const digits = watcher[Field.SocialSecurityNumber] || '';
    const state: ExtendedIdentityFormState = {
      ...(watcher[Field.MiddleName]?.trim() ? { middleName: watcher[Field.MiddleName].trim() } : {}),
      ...(digits ? { socialSecurityNumber: digits } : {}),
    };

    navigate(RoutePath.ExtendedIdentityAddresses, { state });
  };

  return (
    <FormContainer
      title="Help us match your credit profile"
      subtitle="We couldn't find a credit file with the details we have. Extra information is optional, but it helps us match the right profile."
      className={styles.container}
    >
      <Input
        label="Middle name (optional)"
        name={Field.MiddleName}
        placeholder="Middle name"
        value={watcher[Field.MiddleName]}
        onChange={(event) => {
          setValue(Field.MiddleName, event.target.value);
          trigger(Field.MiddleName);
        }}
        errorMessage={errors[Field.MiddleName]?.message}
      />
      <NumberInput
        label="Social Security number"
        placeholder="XXX-XX-XXXX"
        format="###-##-####"
        name={Field.SocialSecurityNumber}
        errorMessage={errors[Field.SocialSecurityNumber]?.message}
        onChange={(event) => {
          setValue(Field.SocialSecurityNumber, event.target.value.replace(NON_DIGITS_PATTERN, ''));
        }}
        onBlur={() => {
          trigger(Field.SocialSecurityNumber);
        }}
        value={watcher[Field.SocialSecurityNumber]}
        dataNeuroLabel="ssn"
      />
      <Button className={styles.primaryButton} onClick={handleContinue}>
        Continue
      </Button>
    </FormContainer>
  );
};

export default ExtendedIdentityCheck;
